import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bug, MoreVertical, Pencil, Search, Trash } from 'lucide-react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import type { IToolApiAction, IToolDetails } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useCreateApiAction } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { ImportSwaggerModal } from './import-swagger-modal';
import { ImportGraphqlModal } from './import-graphql-modal';
import { ImportCurlModal } from './import-curl-modal';
import { ToolApiDeleteActionModal } from './tool-api-delete-action-modal';
import { ToolApiDeleteAllActionsModal } from './tool-api-delete-all-actions-modal';
import {
  ToolApiSetAllActionsEnabledModal,
  bulkEnableTarget,
} from './tool-api-set-all-actions-enabled-modal';
import { getToolActions, getToolIngestReport } from './tool-detail-utils';
import { ToolMethodBadge } from './tool-method-badge';
import { toolsListPaginationClassName, toolsListPaginationControlsClassName } from '../tools-shared';

type TestStatusFilter = 'all' | 'tested' | 'not-tested';
type ActiveFilter = 'all' | 'active' | 'inactive';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export function ToolAPI({ tool }: { tool: IToolDetails }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canEdit = useIsOwner(tool);
  const { mutateAsync: upsertAction } = useCreateApiAction();
  const [search, setSearch] = useState('');
  const [testStatusFilter, setTestStatusFilter] = useState<TestStatusFilter>('all');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [selectedActionId, setSelectedActionId] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [isSetAllEnabledModalOpen, setIsSetAllEnabledModalOpen] = useState(false);
  const [isImportSwaggerOpen, setIsImportSwaggerOpen] = useState(false);
  const [isImportGraphqlOpen, setIsImportGraphqlOpen] = useState(false);
  const [isImportCurlOpen, setIsImportCurlOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Optimistic overrides keyed by actionId; cleared once the refetched detail reflects the value.
  const [enabledOverrides, setEnabledOverrides] = useState<Record<string, boolean>>({});
  const [approvalOverrides, setApprovalOverrides] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const actions = getToolActions(tool);
  const ingestReport = getToolIngestReport(tool);

  const isActionEnabled = (action: IToolApiAction & { actionId: string }) => {
    if (action.actionId in enabledOverrides) return enabledOverrides[action.actionId];
    return action.Enabled ?? action.enabled ?? true;
  };

  // Server state only: a pending row toggle must not change what the bulk button offers.
  const bulkTarget = bulkEnableTarget(actions, (action) => action.Enabled ?? action.enabled ?? true);

  const isActionApprovalRequired = (action: IToolApiAction & { actionId: string }) => {
    if (action.actionId in approvalOverrides) return approvalOverrides[action.actionId];
    return !!action.RequiresApproval;
  };

  const clearOverrides = (actionId: string) => {
    const drop = (prev: Record<string, boolean>) => {
      const copy = { ...prev };
      delete copy[actionId];
      return copy;
    };
    setEnabledOverrides(drop);
    setApprovalOverrides(drop);
  };

  const patchAction = async (
    action: IToolApiAction & { actionId: string },
    patch: { enabled?: boolean; requires_approval?: boolean }
  ) => {
    if (!canEdit || !projectKey || togglingId) return;
    setTogglingId(action.actionId);
    if (patch.enabled !== undefined) {
      setEnabledOverrides((prev) => ({ ...prev, [action.actionId]: patch.enabled as boolean }));
    }
    if (patch.requires_approval !== undefined) {
      setApprovalOverrides((prev) => ({
        ...prev,
        [action.actionId]: patch.requires_approval as boolean,
      }));
    }
    try {
      await upsertAction({
        toolId: tool.id,
        actionId: action.actionId,
        projectKey,
        payload: {
          name: action.Name || action.actionId,
          description: action.Description || '',
          method: (action.Method || 'GET').toUpperCase(),
          endpoint: action.Endpoint || '',
          input_parameters: (action.InputParameters as Record<string, unknown>) || {},
          output_parameters: (action.OutputParameters as Record<string, unknown>) || {},
          is_open_tool: tool.is_open_tool,
          enabled: patch.enabled ?? isActionEnabled(action),
          requires_approval: patch.requires_approval ?? isActionApprovalRequired(action),
          // Preserve GraphQL fields so an enable/approval toggle doesn't drop them.
          ...((action.Method || '').toUpperCase() === 'GRAPHQL'
            ? {
                graphql_query: action.GraphQLQuery || '',
                graphql_operation_type: action.GraphQLOperationType,
              }
            : {}),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['tools', 'detail', tool.id, projectKey] });
      // Drop the override so the row reads the freshly-refetched server value.
      clearOverrides(action.actionId);
    } catch (error) {
      // Rollback the optimistic flip.
      clearOverrides(action.actionId);
      toast({
        variant: 'destructive',
        title: t('ACTION_FAILED') || 'Action failed',
        description: error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleEnabled = (action: IToolApiAction & { actionId: string }, next: boolean) =>
    patchAction(action, { enabled: next });

  const handleToggleApproval = (action: IToolApiAction & { actionId: string }, next: boolean) =>
    patchAction(action, { requires_approval: next });

  const testedLabel = t('TESTED') || 'Tested';
  const notTestedLabel = t('NOT_TESTED') || 'Not Tested';
  const getTestStatusLabel = (isTested: boolean) => (isTested ? testedLabel : notTestedLabel);

  // Filter/search FIRST, then paginate.
  const filteredActions = useMemo(() => {
    const normalized = search.trim().toLowerCase();

    return actions.filter((action) => {
      const matchesTestStatus =
        testStatusFilter === 'all'
          ? true
          : testStatusFilter === 'tested'
            ? !!action.TestStatus
            : !action.TestStatus;
      if (!matchesTestStatus) return false;

      const enabled = action.Enabled ?? action.enabled ?? true;
      const matchesActive =
        activeFilter === 'all' ? true : activeFilter === 'active' ? enabled : !enabled;
      if (!matchesActive) return false;

      if (!normalized) return true;

      return (
        (action.Name || '').toLowerCase().includes(normalized) ||
        (action.actionId || '').toLowerCase().includes(normalized) ||
        (action.Endpoint || '').toLowerCase().includes(normalized) ||
        (action.Method || '').toLowerCase().includes(normalized)
      );
    });
  }, [actions, search, testStatusFilter, activeFilter]);

  const totalCount = filteredActions.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, pageCount);

  // Reset to page 1 whenever the filtered result set changes.
  useEffect(() => {
    setPage(1);
  }, [search, testStatusFilter, activeFilter, pageSize]);

  const pagedActions = useMemo(
    () => filteredActions.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredActions, currentPage, pageSize]
  );

  const getActionEditPath = (actionId: string) =>
    `/tool-details/${encodeURIComponent(tool.id)}/create-api?actionId=${encodeURIComponent(actionId)}${tool.is_open_tool ? '&isBlocksTool=true' : ''}`;

  const renderActionMenu = (actionId: string) => (
    <div
      data-tool-api-action-menu
      className="inline-flex"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={!canEdit}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={!canEdit} onSelect={() => navigate(getActionEditPath(actionId))}>
            <Pencil className="h-4 w-4" />
            {t('EDIT') || 'Edit'}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!canEdit}
            onSelect={() => navigate(`${getActionEditPath(actionId)}&step=4`)}
          >
            <Bug className="h-4 w-4" />
            {t('DEBUG') || 'Debug'}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={dropdownMenuItemDestructiveClass}
            disabled={!canEdit}
            onSelect={(event) => {
              event.preventDefault();
              setSelectedActionId(actionId);
              setIsDeleteModalOpen(true);
            }}
          >
            <Trash className="h-4 w-4" />
            {t('DELETE') || 'Delete'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const handleActionNavigate = (editPath: string, event: React.MouseEvent | React.KeyboardEvent) => {
    if (!canEdit) return;
    if ((event.target as HTMLElement).closest('[data-tool-api-action-menu]')) {
      return;
    }
    navigate(editPath);
  };

  return (
    <Card className="min-w-0 overflow-hidden shadow-none">
      <CardHeader className="min-w-0 gap-4">
        <div className="flex w-full min-w-0 flex-col gap-4">
          <div className="flex w-full min-w-0 flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 lg:flex-1">
              <div className="relative w-full sm:w-[260px] sm:shrink-0">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('SEARCH') || 'Search...'}
                  className="!h-9 w-full rounded-md py-0 pl-9"
                />
              </div>
              <div className="w-full sm:w-[160px] sm:shrink-0">
                <Select
                  value={testStatusFilter}
                  onValueChange={(value) => setTestStatusFilter(value as TestStatusFilter)}
                >
                  <SelectTrigger className="!h-9 w-full rounded-md py-0">
                    <SelectValue placeholder={t('TEST_STATUS') || 'Test Status'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('TEST_STATUS') || 'Test Status'}</SelectItem>
                    <SelectItem value="tested">{testedLabel}</SelectItem>
                    <SelectItem value="not-tested">{notTestedLabel}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-[160px] sm:shrink-0">
                <Select value={activeFilter} onValueChange={(value) => setActiveFilter(value as ActiveFilter)}>
                  <SelectTrigger className="!h-9 w-full rounded-md py-0">
                    <SelectValue placeholder={t('ACTIVE_STATUS') || 'Active Status'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('ACTIVE_STATUS') || 'Active Status'}</SelectItem>
                    <SelectItem value="active">{t('ACTIVE') || 'Active'}</SelectItem>
                    <SelectItem value="inactive">{t('INACTIVE') || 'Inactive'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {canEdit ? (
              <div className="flex w-full shrink-0 flex-col gap-3 sm:flex-row sm:flex-wrap lg:w-auto lg:justify-end">
                <Button
                  variant="outline"
                  className="h-9 w-full rounded-md px-4 sm:min-w-0 sm:flex-1 lg:w-auto lg:flex-none"
                  onClick={() => setIsImportSwaggerOpen(true)}
                >
                  {t('IMPORT_FROM_SWAGGER') || 'Import from Swagger'}
                </Button>
                <Button
                  variant="outline"
                  className="h-9 w-full rounded-md px-4 sm:min-w-0 sm:flex-1 lg:w-auto lg:flex-none"
                  onClick={() => setIsImportGraphqlOpen(true)}
                >
                  {t('IMPORT_FROM_GRAPHQL', { defaultValue: 'Import from GraphQL' })}
                </Button>
                <Button
                  variant="outline"
                  className="h-9 w-full rounded-md px-4 sm:min-w-0 sm:flex-1 lg:w-auto lg:flex-none"
                  onClick={() => setIsImportCurlOpen(true)}
                >
                  {t('IMPORT_FROM_CURL', { defaultValue: 'Import from cURL' })}
                </Button>
                <Button
                  className="h-9 w-full rounded-md px-4 sm:min-w-0 sm:flex-1 lg:w-auto lg:flex-none"
                  onClick={() =>
                    navigate(
                      `/tool-details/${encodeURIComponent(tool.id)}/create-api${tool.is_open_tool ? '?isBlocksTool=true' : ''}`
                    )
                  }
                >
                  {t('CREATE_API') || 'Create API'}
                </Button>
                <Button
                  variant="outline"
                  className="h-9 w-full rounded-md px-4 sm:min-w-0 sm:flex-1 lg:w-auto lg:flex-none"
                  disabled={!bulkTarget || togglingId !== null}
                  onClick={() => setIsSetAllEnabledModalOpen(true)}
                >
                  {bulkTarget?.enabled === false
                    ? t('DISABLE_ALL') || 'Disable all'
                    : t('ENABLE_ALL') || 'Enable all'}
                </Button>
                <Button
                  variant="outline"
                  className="h-9 w-full rounded-md px-4 text-destructive hover:bg-destructive/10 hover:text-destructive sm:min-w-0 sm:flex-1 lg:w-auto lg:flex-none"
                  disabled={actions.length === 0}
                  onClick={() => setIsDeleteAllModalOpen(true)}
                >
                  {t('DELETE_ALL') || 'Delete All'}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
        {ingestReport ? (
          <div className="text-sm text-muted-foreground">
            {t('INGEST_REPORT_SUMMARY', {
              found: ingestReport.found ?? 0,
              converted: ingestReport.converted ?? 0,
              skipped: Array.isArray(ingestReport.skipped)
                ? ingestReport.skipped.length
                : ingestReport.skipped ?? 0,
              defaultValue: 'Last import: {{found}} found, {{converted}} converted, {{skipped}} skipped.',
            })}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="min-w-0">
        {totalCount === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('NO_API_ACTIONS') || 'No API actions available for this tool.'}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-3 lg:hidden">
              {pagedActions.map((action) => {
                const inputParameters = Object.keys(action.InputParameters || {});
                const createdAt = action.CreatedAt ? new Date(action.CreatedAt) : null;
                const editPath = getActionEditPath(action.actionId);

                return (
                  <div
                    key={action.actionId}
                    role={canEdit ? 'button' : undefined}
                    tabIndex={canEdit ? 0 : undefined}
                    className={`rounded-lg border border-border bg-background p-4 ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={(event) => handleActionNavigate(editPath, event)}
                    onKeyDown={(event) => {
                      if (!canEdit) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleActionNavigate(editPath, event);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <ToolMethodBadge method={action.Method} />
                        <span className="min-w-0 truncate font-medium">{action.Name || action.actionId}</span>
                      </div>
                      {renderActionMenu(action.actionId)}
                    </div>

                    <div className="mt-3 space-y-3">
                      <div>
                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                          {t('INPUT_PARAMETERS') || 'Input Parameters'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {inputParameters.length > 0 ? (
                            inputParameters.map((param) => (
                              <Badge key={param} variant="outline" className="max-w-full truncate">
                                {param}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">{t('NONE') || 'None'}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="mb-1 text-xs font-medium text-muted-foreground">
                            {t('TEST_STATUS') || 'Test Status'}
                          </p>
                          <Badge variant={action.TestStatus ? 'default' : 'outline'}>
                            {getTestStatusLabel(!!action.TestStatus)}
                          </Badge>
                        </div>
                        <div
                          onClick={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <p className="mb-1 text-xs font-medium text-muted-foreground">
                            {t('ACTIVE') || 'Active'}
                          </p>
                          <Switch
                            checked={isActionEnabled(action)}
                            disabled={!canEdit || togglingId === action.actionId}
                            onCheckedChange={(checked) => handleToggleEnabled(action, checked)}
                            aria-label={t('ACTIVE') || 'Active'}
                          />
                        </div>
                        <div
                          onClick={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <p className="mb-1 text-xs font-medium text-muted-foreground">
                            {t('ASK_APPROVAL') || 'Ask Approval'}
                          </p>
                          <Checkbox
                            checked={isActionApprovalRequired(action)}
                            disabled={!canEdit || togglingId === action.actionId}
                            onCheckedChange={(checked) => handleToggleApproval(action, checked === true)}
                            aria-label={t('ASK_APPROVAL') || 'Ask Approval'}
                          />
                        </div>
                        <div className="text-right">
                          <p className="mb-1 text-xs font-medium text-muted-foreground">
                            {t('CREATE_TIME') || 'Create Time'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {createdAt && !Number.isNaN(createdAt.getTime())
                              ? createdAt.toLocaleString()
                              : t('UNKNOWN') || 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Table className="hidden min-w-[720px] lg:table" divClassname="hidden lg:block">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">{t('NAME') || 'Name'}</TableHead>
                  <TableHead className="min-w-[220px]">{t('INPUT_PARAMETERS') || 'Input Parameters'}</TableHead>
                  <TableHead className="min-w-[120px]">{t('TEST_STATUS') || 'Test Status'}</TableHead>
                  <TableHead className="min-w-[100px]">{t('ACTIVE') || 'Active'}</TableHead>
                  <TableHead className="min-w-[120px]">{t('ASK_APPROVAL') || 'Ask Approval'}</TableHead>
                  <TableHead className="min-w-[160px]">{t('CREATE_TIME') || 'Create Time'}</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedActions.map((action) => {
                  const inputParameters = Object.keys(action.InputParameters || {});
                  const createdAt = action.CreatedAt ? new Date(action.CreatedAt) : null;
                  const editPath = getActionEditPath(action.actionId);

                  return (
                    <TableRow
                      key={action.actionId}
                      className={canEdit ? 'cursor-pointer' : 'cursor-default'}
                      onClick={(event) => handleActionNavigate(editPath, event)}
                    >
                      <TableCell>
                        <div className="flex min-w-[160px] items-center gap-2">
                          <ToolMethodBadge method={action.Method} />
                          <span className="min-w-0 truncate font-medium">{action.Name || action.actionId}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex min-w-[200px] flex-wrap gap-2">
                          {inputParameters.length > 0 ? (
                            inputParameters.map((param) => (
                              <Badge key={param} variant="outline" className="max-w-[200px] truncate">
                                {param}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">{t('NONE') || 'None'}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={action.TestStatus ? 'default' : 'outline'}>
                          {getTestStatusLabel(!!action.TestStatus)}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <Switch
                          checked={isActionEnabled(action)}
                          disabled={!canEdit || togglingId === action.actionId}
                          onCheckedChange={(checked) => handleToggleEnabled(action, checked)}
                          aria-label={t('ACTIVE') || 'Active'}
                        />
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={isActionApprovalRequired(action)}
                          disabled={!canEdit || togglingId === action.actionId}
                          onCheckedChange={(checked) => handleToggleApproval(action, checked === true)}
                          aria-label={t('ASK_APPROVAL') || 'Ask Approval'}
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {createdAt && !Number.isNaN(createdAt.getTime())
                          ? createdAt.toLocaleString()
                          : t('UNKNOWN') || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-right">{renderActionMenu(action.actionId)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className={toolsListPaginationClassName}>
              <span className="text-sm text-muted-foreground">
                {t('TOOLS_SHOWING_ENTRIES', {
                  from: (currentPage - 1) * pageSize + 1,
                  to: Math.min(currentPage * pageSize, totalCount),
                  total: totalCount,
                }) ||
                  `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} entries`}
              </span>

              <div className={toolsListPaginationControlsClassName}>
                <select
                  value={String(pageSize)}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                  className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm sm:w-auto"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {t('TOOLS_PER_PAGE', { size }) || `${size} / page`}
                    </option>
                  ))}
                </select>

                {totalCount > pageSize ? (
                  <div className="flex w-full items-center gap-2 sm:w-auto">
                    <Button
                      variant="outline"
                      className="flex-1 sm:flex-none"
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage <= 1}
                    >
                      {t('PREVIOUS') || 'Previous'}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 sm:flex-none"
                      onClick={() => setPage((prev) => Math.min(pageCount, prev + 1))}
                      disabled={currentPage >= pageCount}
                    >
                      {t('NEXT') || 'Next'}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )}
      </CardContent>
      <ToolApiDeleteActionModal
        toolId={tool.id}
        actionId={selectedActionId}
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        projectKey={projectKey}
        isBlocksTool={tool.is_open_tool}
      />
      <ToolApiDeleteAllActionsModal
        toolId={tool.id}
        open={isDeleteAllModalOpen}
        onOpenChange={setIsDeleteAllModalOpen}
        projectKey={projectKey}
        isBlocksTool={tool.is_open_tool}
      />
      {bulkTarget ? (
        <ToolApiSetAllActionsEnabledModal
          toolId={tool.id}
          enabled={bulkTarget.enabled}
          count={bulkTarget.count}
          open={isSetAllEnabledModalOpen}
          onOpenChange={setIsSetAllEnabledModalOpen}
          projectKey={projectKey}
          isBlocksTool={tool.is_open_tool}
        />
      ) : null}
      <ImportSwaggerModal
        open={isImportSwaggerOpen}
        onOpenChange={setIsImportSwaggerOpen}
        toolId={tool.id}
        projectKey={projectKey}
      />
      <ImportGraphqlModal
        open={isImportGraphqlOpen}
        onOpenChange={setIsImportGraphqlOpen}
        toolId={tool.id}
        projectKey={projectKey}
      />
      <ImportCurlModal
        open={isImportCurlOpen}
        onOpenChange={setIsImportCurlOpen}
        toolId={tool.id}
        projectKey={projectKey}
        isBlocksTool={tool.is_open_tool}
        baseUrl={tool.APIConfig?.BaseURL || tool.APIConfig?.base_url || tool.base_url || ''}
      />
    </Card>
  );
}
